'use client';

import { FC, useCallback, useContext, useMemo, useState } from 'react';
import type { EditTagsDialogProps } from './EditTagsDialog.types';
import {
  Button,
  ButtonGroup,
  Dialog,
  Flex,
  Input,
  InputChips,
  Separator,
  Text,
  Tooltip,
} from '@/_components';
import { CompanyContext } from '@/_contexts/admin/CompanyProvider';
import { EntityV2Context } from '@/_contexts/admin/EntityV2Provider';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';
import {
  addClientTag,
  addEntityTag,
  updateBulkClientTags,
  updateBulkEntityTags,
} from '@/_api/admin-tags';
import { ENTITY, USER } from './EditTagsDialog.types';
import { VERBIAGE } from './EditTagsDialog.language';
import { RemoveAllTagsDialog } from './components';

/**
 * Dialog Box to edit tags for entities or users selected from the respective table.
 */
export const EditTagsDialog: FC<EditTagsDialogProps> = ({
  isOpen,
  onClose: parentOnClose,
  selectedEntityIds,
  selectedUserIds,
}) => {
  const { addToast } = useContext(ToastContext);
  const { token } = useContext(UserContext);
  const { loadUsersForTable } = useContext(CompanyContext);
  const { loadEntities } = useContext(EntityV2Context);
  const { clientTagOptionsToAdd, entityTagOptionsToAdd, reloadFirm } =
    useContext(FirmSettingsContext);

  const [chipsToAdd, setChipsToAdd] = useState<Set<string>>(new Set());
  const [chipToAddInputText, setChipToAddInputText] = useState<string>('');

  const [chipsToRemove, setChipsToRemove] = useState<Set<string>>(new Set());
  const [chipToRemoveInputText, setChipToRemoveInputText] =
    useState<string>('');

  const [tagToCreate, setTagToCreate] = useState<string>('');

  const [isRemoveAllTagsDialogOpen, setIsRemoveAllTagsDialogOpen] =
    useState(false);

  const [isLoading, setIsLoading] = useState(false);

  if (selectedEntityIds && selectedUserIds) {
    throw new Error('Cannot pass both `selectedEntities` and `selectedUsers`');
  }

  // TODO: use an enum instead of numbers once SOR-14040 is done
  const tagType = selectedEntityIds?.length ? ENTITY : USER;
  const selectedEntityOrUserIds = selectedEntityIds || selectedUserIds;
  const addTag = tagType === ENTITY ? addEntityTag : addClientTag;
  const updateTags =
    tagType === ENTITY ? updateBulkEntityTags : updateBulkClientTags;
  const tagOptionsToAdd = useMemo(
    () =>
      (tagType === ENTITY ? entityTagOptionsToAdd : clientTagOptionsToAdd).map(
        (option) => ({
          ...option,
          isChecked: chipsToAdd.has(option.value || ''),
        }),
      ),
    [clientTagOptionsToAdd, entityTagOptionsToAdd, tagType, chipsToAdd],
  );

  const tagOptionsToRemove = useMemo(
    () =>
      (tagType === ENTITY ? entityTagOptionsToAdd : clientTagOptionsToAdd).map(
        (option) => ({
          ...option,
          isChecked: chipsToRemove.has(option.value || ''),
        }),
      ),
    [clientTagOptionsToAdd, entityTagOptionsToAdd, tagType, chipsToRemove],
  );

  const reloadTable = tagType === ENTITY ? loadEntities : loadUsersForTable;

  // TODO: uncomment when we implement getting tag data for all entities/users
  /*
  const tagsFullyAppliedToSelection = useMemo(
    () =>
      selectedEntitiesOrUsers
        ?.map((entityOrUser) => {
          if (tagType === ENTITY && 'entity_tags' in entityOrUser) {
            return entityOrUser.entity_tags;
          } else if (tagType === USER && 'contact_tags' in entityOrUser) {
            return entityOrUser.contact_tags;
          } else {
            return [];
          }
        })
        .reduce<EntityTag[] | ContactTag[]>((acc, curr) => {
          if (acc.length === 0) {
            return curr;
          }
          return acc.filter((tag) =>
            curr.some((currTag) => currTag.name === tag.name),
          );
        }, []),
    [selectedEntitiesOrUsers, tagType],
  );

  const tagsPartiallyAppliedToSelection = useMemo(() => {
    // For computing set difference of tags applied to all and tags
    // applied to only some, for more intuitive UI understanding.
    // The user assumes that the tags applied to all are also implicitly
    // partially applied, so we don't need to show them twice.
    const tagIdsAppliedToAll = new Set(
      tagsFullyAppliedToSelection?.map((tag) => tag.id),
    );

    return selectedEntitiesOrUsers
      ?.map((entityOrUser) => {
        if (tagType === ENTITY && 'entity_tags' in entityOrUser) {
          return entityOrUser.entity_tags;
        } else if (tagType === USER && 'contact_tags' in entityOrUser) {
          return entityOrUser.contact_tags;
        } else {
          return [];
        }
      })
      .reduce<EntityTag[] | ContactTag[]>(
        (acc, curr) => [
          ...acc,
          ...curr.filter(
            (tag) => !acc.some((existingTag) => existingTag.name === tag.name),
          ),
        ],
        [],
      )
      .filter((tag) => !tagIdsAppliedToAll.has(tag.id));
  }, [selectedEntitiesOrUsers, tagsFullyAppliedToSelection, tagType]);

  const selectionHasSomeTags = useMemo(
    () =>
      Boolean(
        tagsFullyAppliedToSelection?.length ||
          tagsPartiallyAppliedToSelection?.length,
      ),
    [tagsFullyAppliedToSelection, tagsPartiallyAppliedToSelection],
  );

  const appliedTags = useMemo(
    () =>
      new Set([
        ...(tagsFullyAppliedToSelection?.map((tag) => tag.name) || []),
        ...(tagsPartiallyAppliedToSelection?.map((tag) => tag.name) || []),
      ]),
    [tagsFullyAppliedToSelection, tagsPartiallyAppliedToSelection],
  );

  const tagOptionsToRemove = useMemo(
    () => tagOptions.filter((tag) => appliedTags?.has(tag.name || '')),
    [tagOptions, appliedTags],
  );
  */

  const onClose = useCallback(() => {
    parentOnClose();
    setChipsToAdd(new Set());
    setChipsToRemove(new Set());
  }, [parentOnClose, setChipsToAdd, setChipsToRemove]);

  const onApplyTags = useCallback(async () => {
    try {
      setIsLoading(true);
      let tagsWereUpdated = false;

      if (chipsToAdd.size) {
        await updateTags({
          ids: selectedEntityOrUserIds || [],
          tag_names: Array.from(chipsToAdd),
          token,
        });
        tagsWereUpdated = true;
      }

      if (chipsToRemove.size) {
        await updateTags({
          ids: selectedEntityOrUserIds || [],
          tag_names: Array.from(chipsToRemove),
          remove: true,
          token,
        });
        tagsWereUpdated = true;
      }

      if (tagsWereUpdated) await reloadTable();
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setIsLoading(false);
      onClose();
    }
  }, [
    chipsToAdd,
    chipsToRemove,
    reloadTable,
    addToast,
    onClose,
    updateTags,
    selectedEntityOrUserIds,
    token,
  ]);

  const onCreateTag = async () => {
    setIsLoading(true);
    const { error } = await addTag({ new_tag: tagToCreate, token });

    if (error) {
      addToast('error', error, { error });
    } else {
      setChipsToAdd(new Set([...chipsToAdd, tagToCreate]));
      await reloadFirm();
      setTagToCreate('');
    }
    setIsLoading(false);
  };

  const onClearTags = () => {
    setIsRemoveAllTagsDialogOpen(true);
  };

  const onCloseRemoveAllTagsDialog = () => {
    setIsRemoveAllTagsDialogOpen(false);
  };

  if (!selectedEntityOrUserIds) {
    addToast('info', 'No selected users or entities to update!');
    return null;
  }

  return (
    <>
      <Dialog
        isOpen={isOpen}
        onClose={onClose}
        title={`Update Tags (${selectedEntityOrUserIds.length} ${VERBIAGE[tagType][selectedEntityOrUserIds.length > 1 ? 'plural' : 'singular']})`}
      >
        <Dialog.Content>
          <Flex isColumn={true} gap={8}>
            {/* TODO: uncomment when we implement getting tag data for all entities/users */}
            {/* {selectionHasSomeTags && (
              <Flex isColumn={true} gap={4}>
                <Flex isBetween={true} isYCentered={true}>
                  <Text type="small" fontWeight="medium">
                    Current Tags
                  </Text>
                  <Button
                    intent="danger-ghost"
                    size="tiny"
                    value="Clear all tags"
                    onClick={onClearTags}
                  />
                </Flex>
                <Card padding={6}>
                  <Flex isColumn={true} gap={4}>
                    {!!tagsFullyAppliedToSelection?.length && (
                      <Flex isColumn={true} gap={2}>
                        <Text type="tiny" fontWeight="medium" color="secondary">
                          Applied to All
                        </Text>
                        <Flex gap={2}>
                          {tagsFullyAppliedToSelection?.map((tag, key) => (
                            <Pill
                              key={`${key}-all`}
                              intent="secondary"
                              label={tag.name}
                            />
                          ))}
                        </Flex>
                      </Flex>
                    )}
                    {!!tagsPartiallyAppliedToSelection?.length && (
                      <Flex isColumn={true} gap={2}>
                        <Text type="tiny" fontWeight="medium" color="secondary">
                          Partially Applied
                        </Text>
                        <Flex gap={2}>
                          {tagsPartiallyAppliedToSelection?.map((tag, key) => (
                            <Pill
                              key={`${key}-partial`}
                              intent="secondary"
                              label={tag.name}
                            />
                          ))}
                        </Flex>
                      </Flex>
                    )}
                  </Flex>
                </Card>
              </Flex>
            )} */}
            <Tooltip isDisabled={!!tagOptionsToAdd.length}>
              <Tooltip.Trigger>
                <InputChips
                  results={tagOptionsToAdd}
                  chips={chipsToAdd}
                  label="Tags to Add"
                  isDisabled={isLoading || !tagOptionsToAdd?.length}
                  onChange={(event) =>
                    setChipToAddInputText(event.target.value)
                  }
                  setChips={setChipsToAdd}
                  onChipSelect={setChipsToAdd}
                  value={chipToAddInputText}
                  placeholder="Click to select tags"
                />
              </Tooltip.Trigger>
              <Tooltip.Content>
                <Text type="small" color="currentcolor">
                  {`Create new ${VERBIAGE[tagType].singular.toLowerCase()} tags below to add them to ${VERBIAGE[tagType].plural.toLowerCase()}.`}
                </Text>
              </Tooltip.Content>
            </Tooltip>
            <InputChips
              results={tagOptionsToRemove}
              chips={chipsToRemove}
              label="Tags to Remove"
              isDisabled={isLoading || !tagOptionsToRemove?.length}
              onChange={(event) => setChipToRemoveInputText(event.target.value)}
              setChips={setChipsToRemove}
              value={chipToRemoveInputText}
              placeholder="Click to select tags"
              clearable={true}
              persistClearButton={true}
              clearChipsText="Remove all tags"
              onClearChips={onClearTags}
            />
            {/* TODO: uncomment when we implement getting tag data for all entities/users */}
            {/* {!!tagOptionsToRemove.length && (
              <InputChips
                results={tagOptionsToRemove}
                chips={chipsToRemove}
                label="Tags to Remove"
                isDisabled={isLoading || !tagOptionsToRemove?.length}
                onChange={(event) =>
                  setChipToRemoveInputText(event.target.value)
                }
                setChips={setChipsToRemove}
                value={chipToRemoveInputText}
                placeholder="Click to select tags"
              />
            )} */}
            <Separator />
            <Flex yAlign="end" gap={4}>
              <Input
                label="Create New Tag"
                isDisabled={isLoading}
                onChange={(event) => setTagToCreate(event.target.value)}
                value={tagToCreate}
                placeholder="Tag name"
              />
              <Button
                intent="secondary"
                value="Create tag"
                onClick={onCreateTag}
              />
            </Flex>
          </Flex>
        </Dialog.Content>
        <Dialog.Footer>
          <Flex xAlign="end">
            <ButtonGroup>
              <Button intent="secondary" value="Cancel" onClick={onClose} />
              <Button
                intent="primary"
                value="Apply tags"
                onClick={onApplyTags}
              />
            </ButtonGroup>
          </Flex>
          {/* TODO: use the following instead of the above once "View All Tags" Dialog implemented */}
          {/* <Flex isBetween={true}>
            <Button intent="ghost" value="View All Tags" />
            <ButtonGroup>
              <Button intent="secondary" value="Cancel" onClick={onClose} />
              <Button
                intent="primary"
                value="Apply tags"
                onClick={onApplyTags}
              />
            </ButtonGroup>
          </Flex> */}
        </Dialog.Footer>
      </Dialog>
      <RemoveAllTagsDialog
        entityOrUserIds={selectedEntityOrUserIds}
        isOpen={isRemoveAllTagsDialogOpen}
        onClose={onCloseRemoveAllTagsDialog}
        parentOnClose={onClose}
        tagType={tagType}
      />
    </>
  );
};
